import { useState } from 'react';

type Props = {
  score: number;
  userVote: 1 | -1 | null;
  onVote: (value: -1 | 0 | 1) => Promise<void>;
  /** Compact = inline single-row chevrons (used in pills). */
  compact?: boolean;
  disabled?: boolean;
};

/** Pair of ▲/▼ buttons with the current score between them. Clicking
 *  the same direction twice clears the vote; clicking the opposite
 *  switches sides. Optimistic local busy flag while the request flies. */
export default function VoteControl({ score, userVote, onVote, compact = true, disabled }: Props) {
  const [busy, setBusy] = useState(false);

  async function cast(intent: 1 | -1) {
    if (busy || disabled) return;
    const next: -1 | 0 | 1 = userVote === intent ? 0 : intent;
    setBusy(true);
    try {
      await onVote(next);
    } finally {
      setBusy(false);
    }
  }

  const upStyle: React.CSSProperties = {
    background: 'none',
    border: 0,
    cursor: busy || disabled ? 'default' : 'pointer',
    color: userVote === 1 ? 'var(--vote-fg-up)' : 'var(--vote-fg-idle)',
    fontSize: compact ? 11 : 13,
    padding: '0 4px',
    lineHeight: 1,
    fontWeight: 600,
    opacity: busy ? 0.5 : 1,
  };
  const downStyle: React.CSSProperties = { ...upStyle, color: userVote === -1 ? 'var(--vote-fg-down)' : 'var(--vote-fg-idle)' };
  const scoreStyle: React.CSSProperties = {
    minWidth: 18,
    textAlign: 'center',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    fontSize: compact ? 11 : 13,
    color: score > 0 ? 'var(--fg-accent)' : score < 0 ? 'var(--fg-error)' : 'var(--fg-muted)',
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <button type="button" onClick={() => cast(1)} disabled={busy || disabled} aria-label="Upvote" style={upStyle}>▲</button>
      <span style={scoreStyle}>{score > 0 ? `+${score}` : score}</span>
      <button type="button" onClick={() => cast(-1)} disabled={busy || disabled} aria-label="Downvote" style={downStyle}>▼</button>
    </span>
  );
}
